import { useState, useCallback } from "react";
import axios from "axios";

export default function useCities() {
  const [cities, setCities] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [currentCity, setCurrentCity] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Distance formula (Haversine)
  const getDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (val) => (val * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;

    return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // ✅ Find nearest city
  const findNearestCity = (cityList, userLat, userLng) => {
    let nearest = null;
    let minDist = Infinity;

    cityList.forEach((city) => {
      const cLat = parseFloat(city.latitude);
      const cLng = parseFloat(city.longitude);
      if (isNaN(cLat) || isNaN(cLng)) return;

      const dist = getDistance(userLat, userLng, cLat, cLng);
      if (dist < minDist) {
        minDist = dist;
        nearest = city;
      }
    });

    return nearest;
  };

  // ✅ GET ALL STRICTLY DATABASE CITIES FROM BACKEND (Option A: 100% Database Only)
  const getAllCities = useCallback(async () => {
    try {
      setLoading(true);

      // 1. Fetch cities directly from database API
      const [citiesRes, labsRes] = await Promise.all([
        axios.get("https://admin.medrphalabs.com/api/Lab/GetCities").catch(() => ({ data: [] })),
        axios.get("https://admin.medrphalabs.com/api/Lab").catch(() => ({ data: [] }))
      ]);

      const apiCities = Array.isArray(citiesRes.data) ? citiesRes.data : (Array.isArray(citiesRes.data?.data) ? citiesRes.data.data : []);
      const labsList = Array.isArray(labsRes.data) ? labsRes.data : (Array.isArray(labsRes.data?.data) ? labsRes.data.data : []);

      const dynamicCityMap = new Map();

      // Add backend cities from GetCities (Database Table)
      apiCities.forEach((c) => {
        const name = (c.name || c.cityName || "").trim();
        if (name) {
          dynamicCityMap.set(name.toLowerCase(), {
            name: name,
            cityName: name,
            latitude: parseFloat(c.latitude) || 28.5355,
            longitude: parseFloat(c.longitude) || 77.3910,
            isDynamic: true,
          });
        }
      });

      // Also ensure any partner lab's registered city from the database is included
      labsList.forEach((lab) => {
        const addr = (lab.address || "").toLowerCase();
        const labName = (lab.lab_name || lab.labName || "").toLowerCase();
        const lat = parseFloat(lab.latitude);
        const lng = parseFloat(lab.longnitude || lab.longitude);

        if (addr.includes("noida") || labName.includes("noida")) {
          if (!dynamicCityMap.has("noida")) {
            dynamicCityMap.set("noida", {
              name: "Noida",
              cityName: "Noida",
              latitude: lat || 28.5916,
              longitude: lng || 77.3186,
              isDynamic: true,
            });
          }
        }
      });

      const dbCitiesOnly = Array.from(dynamicCityMap.values());
      setCities(dbCitiesOnly);
      console.log("🏙️ 100% Database Cities loaded:", dbCitiesOnly.length, dbCitiesOnly);

      const saved = JSON.parse(localStorage.getItem("selectedCity"));
      if (saved) {
        setCurrentCity(saved);
      }
    } catch (err) {
      console.warn("Database cities fetch error:", err);
      setCities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ SEARCH CITIES (Strictly Database Only)
  const searchCities = useCallback((query) => {
    if (!query || !query.trim()) {
      setSuggestions([]);
      return;
    }

    const q = query.toLowerCase().trim();
    const matches = cities.filter((c) => {
      const name = (c.name || c.cityName || "").toLowerCase();
      return name.includes(q);
    });

    setSuggestions(matches);
  }, [cities]);

  // ✅ MANUAL SELECT
  const selectCity = (city) => {
    const cityName = city?.cityName || city?.name;
    const cityData = {
      name: cityName,
      cityName: cityName,
      latitude: city?.latitude,
      longitude: city?.longitude,
    };

    setCurrentCity(cityData);
    console.log("👉 Selected City (Manual) 👉", cityData);
  };

  return {
    cities,
    suggestions,
    currentCity,
    loading,
    setSuggestions,
    getAllCities,
    searchCities,
    selectCity,
    findNearestCity,
  };
}